import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { encryptId } from "../../../../_metronic/helpers";
import { Collaborator } from "app/types/collaborator";

type Props = {
  board: Collaborator[];
  totalRecords: number;
  pageSize: number;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
};

const CardList: FC<Props> = ({
  board,
  totalRecords,
  pageSize,
  currentPage,
  setCurrentPage,
}) => {
  const history = useHistory();
  const [disableNavigation, setDisableNavigation] = useState(true);
  const totalPages = useMemo(() => Math.ceil(totalRecords / pageSize), [totalRecords, pageSize]);

  const pages = useMemo(() => {
    let first = currentPage - 3;
    if (first < 1) first = 0;
    let end = currentPage + 3;
    if (end > totalPages)
      end = totalPages;
    return Array.from({ length: (end - first) }, (_, index) => first + index);
  }, [currentPage, totalPages]);

  useEffect(() => {
    setDisableNavigation(false);
  }, [board]);
  const handleFirst = () => {
    setDisableNavigation(true);
    setCurrentPage(1);
  };
  const handlePrev = () => {
    setDisableNavigation(true);
    setCurrentPage!((prev) => prev - 1);
  };
  const handleNext = () => {
    setDisableNavigation(true);
    setCurrentPage!((prev) => prev + 1);
  };
  const handleLast = () => {
    setDisableNavigation(true);
    const finalPage = Math.ceil(totalRecords! / pageSize!)
    setCurrentPage!(finalPage);
  };
  return (
    <div className="container mt-5">
      <section>
        <div className="row">
          {board.map((item, index) => (
            <div
              key={index}
              className="col-md-6 col-lg-6 col-xl-4 col-xxl-4 mb-4 mt-4"
            >
              <div
                className="card border borders-card-board cursor-pointer"
                onClick={() =>
                  history.push(
                    `/recursos_humanos/colaboradores/edit/${encryptId(
                      item.id?.toString() || "0"
                    )}`
                  )
                }
              >
                <div className="card-body d-flex p-5">
                  <div className="me-4">
                    <div
                      className="symbol"
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        objectFit: 'cover',
                        objectPosition: 'center', // Centra la imagen
                      }}
                    >
                      <img
                        src={item.photo || `${process.env.PUBLIC_URL}/media/avatars/photo.png`}
                        alt="Collaborator Name"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                    <div className="fs-6 text-success fw-bold mb-2 text-center mt-2">
                      {item.status || "No registrado"}
                    </div>
                  </div>
                  <div className="d-flex flex-column justify-content-center">
                    <a className="fs-4 text-gray-800 text-hover-primary fw-bolder mb-2"
                      style={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        maxWidth: "150px",
                      }}>
                      {item.fullName
                        ? item.fullName
                        : "No registrado"}
                    </a>
                    <div className="fw-bold text-gray-400 mb-2">
                      {item.perfil || "Perfil no registrado"}
                    </div>
                    <div className="fw-bold text-gray-400 mb-2">
                      {item.departamento || "Departamento no registrado"}
                    </div>
                    <div className="fs-6 fw-bold text-gray-500">
                      Creado: {new Date(item.createdAt!).toLocaleDateString('es-MX') || "Sin fecha"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Paginación */}
        <div className="row justify-content-center">
          <nav aria-label="Navegación de página">
            <ul className="pagination">
              <li
                className="page-item"
              >
                <button
                  className="page-link"
                  onClick={handleFirst}
                  disabled={currentPage === 1 || disableNavigation}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" role="presentation"><path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z"></path><path fill="none" d="M24 24H0V0h24v24z"></path></svg>
                </button>
              </li>
              <li
                className="page-item"
              >
                <button
                  className="page-link"
                  onClick={handlePrev}
                  disabled={currentPage === 1 || disableNavigation}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" role="presentation"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path><path d="M0 0h24v24H0z" fill="none"></path></svg>
                </button>
              </li>
              {pages[0] !== 0 && (
                <li
                  className="page-item"
                >
                  <div
                    className="page-dot-dot-dot"
                  >
                    ...
                  </div>
                </li>
              )}
              {pages.map((i) => (
                <li
                  key={i}
                  className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(i + 1)}
                    disabled={disableNavigation}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}
              {pages[pages.length - 1] !== totalPages - 1 && (
                <li
                  className="page-item"
                >
                  <div
                    className="page-dot-dot-dot"
                  >
                    ...
                  </div>
                </li>
              )}
              <li
                className="page-item"
              >
                <button
                  className="page-link"
                  onClick={handleNext}
                  disabled={disableNavigation}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" role="presentation"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path><path d="M0 0h24v24H0z" fill="none"></path></svg>
                </button>
              </li>
              <li
                className="page-item"
              >
                <button
                  className="page-link"
                  onClick={handleLast}
                  disabled={disableNavigation}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" role="presentation"><path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z"></path><path fill="none" d="M0 0h24v24H0V0z"></path></svg>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </section >
    </div >
  );
};

export { CardList };
